/**
 * couponsSlice — Redux Toolkit slice for admin coupon management.
 *
 * State shape
 * -----------
 * list:
 *   - items: CouponDTO[]        — current page of coupons (list view)
 *   - total: number             — total count from the API
 *   - page: number              — current page
 *   - perPage: number           — page size
 *   - isLoading: boolean        — fetch in progress
 *   - listError: string | null  — last list fetch error
 * editor:
 *   - coupon: CouponDTO | null  — coupon currently open in the editor
 * loading: per-operation booleans for editor operations
 * error:   per-operation string | null for editor operations
 *
 * Thunks
 * ------
 * - fetchCoupons(params)      — GET /api/v1/admin/coupons
 * - fetchCoupon(id)           — GET /api/v1/admin/coupons/:id
 * - createCoupon(payload)     — POST /api/v1/admin/coupons
 * - updateCoupon({id, ...})   — PUT /api/v1/admin/coupons/:id
 * - deleteCoupon(id)          — DELETE /api/v1/admin/coupons/:id
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only page-level containers do.
 *
 * CON-PROMO-001 / CON-PROMO-002.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createAdminCoupon,
  deleteAdminCoupon,
  fetchAdminCoupon,
  fetchAdminCoupons,
  updateAdminCoupon,
} from '@dfq/shared/api/admin/coupons';
import { isApiError } from '@dfq/shared/api/_envelope';
import type { CouponDTO, CreateCouponPayload, FetchCouponsParams, UpdateCouponPayload } from '@dfq/shared/types/coupons';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type CouponLoadingKey = 'fetchCoupon' | 'createCoupon' | 'updateCoupon' | 'deleteCoupon';

export interface CouponsState {
  // List view state (CON-PROMO-001)
  items: CouponDTO[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  listError: string | null;
  // Editor state (CON-PROMO-002)
  editor: {
    coupon: CouponDTO | null;
  };
  loading: Record<CouponLoadingKey, boolean>;
  error: Record<CouponLoadingKey, string | null>;
}

const initialState: CouponsState = {
  // List
  items: [],
  total: 0,
  page: 1,
  perPage: 25,
  isLoading: false,
  listError: null,
  // Editor
  editor: {
    coupon: null,
  },
  loading: {
    fetchCoupon: false,
    createCoupon: false,
    updateCoupon: false,
    deleteCoupon: false,
  },
  error: {
    fetchCoupon: null,
    createCoupon: null,
    updateCoupon: null,
    deleteCoupon: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks — list
// ---------------------------------------------------------------------------

export const fetchCoupons = createAsyncThunk(
  'coupons/fetchCoupons',
  async (params: FetchCouponsParams, { rejectWithValue }) => {
    try {
      return await fetchAdminCoupons(params);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch coupons';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Thunks — editor
// ---------------------------------------------------------------------------

export const fetchCoupon = createAsyncThunk(
  'coupons/fetchCoupon',
  async (id: number, { rejectWithValue }) => {
    try {
      return await fetchAdminCoupon(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch coupon';
      return rejectWithValue(msg);
    }
  },
);

export const createCoupon = createAsyncThunk(
  'coupons/createCoupon',
  async (payload: CreateCouponPayload, { rejectWithValue }) => {
    try {
      return await createAdminCoupon(payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, errors: err.errors });
      }
      const msg = err instanceof Error ? err.message : 'Failed to create coupon';
      return rejectWithValue({ message: msg, errors: undefined });
    }
  },
);

export const updateCoupon = createAsyncThunk(
  'coupons/updateCoupon',
  async (
    { id, ...payload }: { id: number } & UpdateCouponPayload,
    { rejectWithValue },
  ) => {
    try {
      return await updateAdminCoupon(id, payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, errors: err.errors });
      }
      const msg = err instanceof Error ? err.message : 'Failed to update coupon';
      return rejectWithValue({ message: msg, errors: undefined });
    }
  },
);

export const deleteCoupon = createAsyncThunk(
  'coupons/deleteCoupon',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAdminCoupon(id);
      return id;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to delete coupon';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const couponsSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    clearEditor(state) {
      state.editor.coupon = null;
      state.error.fetchCoupon = null;
      state.error.createCoupon = null;
      state.error.updateCoupon = null;
    },
  },
  extraReducers: (builder) => {
    // List thunk
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.isLoading = true;
        state.listError = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.perPage = action.payload.perPage;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.isLoading = false;
        state.listError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch coupons';
      });

    // Editor thunks
    builder
      .addCase(fetchCoupon.pending, (state) => {
        state.loading.fetchCoupon = true;
        state.error.fetchCoupon = null;
      })
      .addCase(fetchCoupon.fulfilled, (state, action) => {
        state.loading.fetchCoupon = false;
        state.editor.coupon = action.payload;
      })
      .addCase(fetchCoupon.rejected, (state, action) => {
        state.loading.fetchCoupon = false;
        state.error.fetchCoupon = (action.payload as string) ?? action.error.message ?? 'Failed';
      });

    builder
      .addCase(createCoupon.pending, (state) => {
        state.loading.createCoupon = true;
        state.error.createCoupon = null;
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.loading.createCoupon = false;
        state.editor.coupon = action.payload;
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.loading.createCoupon = false;
        const payload = action.payload as { message: string } | undefined;
        state.error.createCoupon = payload?.message ?? action.error.message ?? 'Failed';
      });

    builder
      .addCase(updateCoupon.pending, (state) => {
        state.loading.updateCoupon = true;
        state.error.updateCoupon = null;
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.loading.updateCoupon = false;
        state.editor.coupon = action.payload;
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.loading.updateCoupon = false;
        const payload = action.payload as { message: string } | undefined;
        state.error.updateCoupon = payload?.message ?? action.error.message ?? 'Failed';
      });

    builder
      .addCase(deleteCoupon.pending, (state) => {
        state.loading.deleteCoupon = true;
        state.error.deleteCoupon = null;
      })
      .addCase(deleteCoupon.fulfilled, (state) => {
        state.loading.deleteCoupon = false;
        state.editor.coupon = null;
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.loading.deleteCoupon = false;
        state.error.deleteCoupon = (action.payload as string) ?? action.error.message ?? 'Failed';
      });
  },
});

export const { clearEditor: clearCouponEditor } = couponsSlice.actions;
export const couponsReducer = couponsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface CouponsRootState {
  coupons: CouponsState;
}

export const selectCoupons = (state: CouponsRootState): CouponDTO[] =>
  state.coupons?.items ?? [];

export const selectCouponsTotal = (state: CouponsRootState): number =>
  state.coupons?.total ?? 0;

export const selectCouponsPage = (state: CouponsRootState): number =>
  state.coupons?.page ?? 1;

export const selectCouponsPerPage = (state: CouponsRootState): number =>
  state.coupons?.perPage ?? 25;

export const selectCouponsLoading = (state: CouponsRootState): boolean =>
  state.coupons?.isLoading ?? false;

export const selectCouponsError = (state: CouponsRootState): string | null =>
  state.coupons?.listError ?? null;
